/**
 * Pagina PUBBLICA della videochiamata: /call/<token>
 *
 * È la pagina white-label: porta il logo e il colore del CLIENTE, e dentro
 * ospita il video (Jitsi). Chiunque abbia il link entra, senza account —
 * basta scrivere il proprio nome.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Video, AlertTriangle, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { videoApi, type PublicRoomInfo } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';

/** Carica una sola volta lo script dell'API Jitsi dal nostro server video. */
function loadJitsiScript(domain: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const w = window as unknown as { JitsiMeetExternalAPI?: unknown };
    if (w.JitsiMeetExternalAPI) return resolve();
    const existing = document.getElementById('jitsi-external-api');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('script')));
      return;
    }
    const s = document.createElement('script');
    s.id = 'jitsi-external-api';
    s.src = `https://${domain}/external_api.js`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Impossibile caricare il servizio video'));
    document.body.appendChild(s);
  });
}

export function CallPage() {
  const { token } = useParams<{ token: string }>();
  const [room, setRoom] = useState<PublicRoomInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [joining, setJoining] = useState(false);
  const [inCall, setInCall] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<{ dispose: () => void } | null>(null);

  useEffect(() => {
    if (!token) return;
    videoApi.publicRoom(token)
      .then(({ room: r }) => {
        setRoom(r);
        document.title = `${r.title} · ${r.organizer_name}`;
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Videochiamata non trovata'));
    return () => { apiRef.current?.dispose(); };
  }, [token]);

  async function join() {
    if (!token || !room || !name.trim()) return;
    setJoining(true);
    setError(null);
    try {
      const { jwt, roomName, domain } = await videoApi.join(token, name.trim());
      await loadJitsiScript(domain);
      setInCall(true);

      // il contenitore esiste solo dopo il render: aspettiamo un tick
      await new Promise((r) => setTimeout(r, 0));
      const w = window as unknown as {
        JitsiMeetExternalAPI: new (domain: string, opts: Record<string, unknown>) => { dispose: () => void; addEventListener: (e: string, cb: () => void) => void };
      };
      const api = new w.JitsiMeetExternalAPI(domain, {
        roomName,
        jwt,
        parentNode: containerRef.current,
        width: '100%',
        height: '100%',
        userInfo: { displayName: name.trim() },
        configOverwrite: {
          prejoinPageEnabled: false,      // il "prejoin" lo facciamo noi, brandizzato
          disableDeepLinking: true,       // niente "apri nell'app Jitsi"
          startWithAudioMuted: false,
          startWithVideoMuted: false,
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          SHOW_BRAND_WATERMARK: false,
          SHOW_POWERED_BY: false,
          DEFAULT_BACKGROUND: '#101014',
          MOBILE_APP_PROMO: false,
          MOBILE_DYNAMIC_LINK: undefined,
        },
      });
      api.addEventListener('readyToClose', () => {
        api.dispose();
        apiRef.current = null;
        setInCall(false);
      });
      apiRef.current = api;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message || 'Ingresso fallito');
      setInCall(false);
    } finally {
      setJoining(false);
    }
  }

  // ---- schermata di attesa / errori -----------------------------------------
  if (!inCall) {
    const brand = room?.brand_color ?? '#6366f1';
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
           style={{ background: `linear-gradient(135deg, ${brand}, #7c3aed)` }}>
        <div className="w-full max-w-md">
          <div className="bg-background rounded-2xl shadow-2xl p-7">
            {!room && !error && (
              <div className="flex items-center justify-center py-14 text-muted-foreground gap-2">
                <Loader2 className="h-5 w-5 animate-spin" /> Un attimo…
              </div>
            )}

            {error && !room && (
              <div className="text-center py-10">
                <AlertTriangle className="h-10 w-10 mx-auto text-amber-500 mb-3" />
                <p className="font-medium">{error}</p>
                <p className="text-sm text-muted-foreground mt-2">Controlla il link ricevuto o chiedi all'organizzatore.</p>
              </div>
            )}

            {room?.ended && (
              <div className="text-center py-10">
                <div className="text-3xl mb-3">👋</div>
                <p className="font-medium">Questa videochiamata è terminata.</p>
              </div>
            )}

            {room && !room.ended && (
              <>
                {/* identità del cliente */}
                <div className="text-center">
                  {room.logo_url ? (
                    <img src={room.logo_url} alt="" className="h-12 mx-auto object-contain mb-4" />
                  ) : (
                    <div className="h-12 w-12 mx-auto rounded-xl flex items-center justify-center mb-4"
                         style={{ backgroundColor: `${brand}1a` }}>
                      <Video className="h-6 w-6" style={{ color: brand }} />
                    </div>
                  )}
                  <h1 className="text-xl font-bold leading-tight">{room.title}</h1>
                  <p className="text-sm text-muted-foreground mt-1">con {room.organizer_name}</p>
                  {room.starts_at && (
                    <p className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      {format(new Date(room.starts_at), "EEEE d MMMM 'alle' HH:mm", { locale: it })}
                    </p>
                  )}
                </div>

                <div className="mt-6 space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="call-name">Come ti chiami?</Label>
                    <Input
                      id="call-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') void join(); }}
                      placeholder="Il tuo nome"
                      autoFocus
                    />
                  </div>
                  {error && <p className="text-sm text-destructive">{error}</p>}
                  <Button
                    className="w-full h-11 text-white"
                    style={{ backgroundColor: brand }}
                    disabled={!name.trim() || joining}
                    onClick={() => void join()}
                  >
                    {joining ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Video className="h-4 w-4 mr-2" />}
                    Entra nella videochiamata
                  </Button>
                  <p className="text-[11px] text-center text-muted-foreground">
                    Ti verranno chiesti i permessi per microfono e telecamera.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- chiamata in corso -----------------------------------------------------
  return (
    <div className="h-screen w-screen bg-[#101014] flex flex-col">
      {room && (
        <div className="h-12 flex items-center gap-3 px-4 shrink-0" style={{ backgroundColor: room.brand_color }}>
          {room.logo_url
            ? <img src={room.logo_url} alt="" className="h-6 object-contain" />
            : <Video className="h-4 w-4 text-white" />}
          <span className="text-white text-sm font-semibold truncate">{room.title}</span>
        </div>
      )}
      <div ref={containerRef} className="flex-1 min-h-0" />
    </div>
  );
}
